import { useCallback, useEffect, useMemo } from "react";

import { useSearchParams } from "react-router-dom";

/**
 * Keeps a page tab in the URL so tab changes participate in browser history.
 *
 * @param {object} options Hook options.
 * @param {string} options.defaultTab Tab used when the URL has no valid tab.
 * @param {string} [options.paramName="tab"] Query parameter that stores the tab.
 * @param {string[]} options.validTabs Tabs that may be selected.
 * @returns {[string, (tab: string, options?: { replace?: boolean }) => void]}
 *   The active tab and a history-aware tab setter.
 */
const useRouteTab = ({ defaultTab, paramName = "tab", validTabs }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabSet = useMemo(() => new Set(validTabs), [validTabs]);
  const requestedTab = searchParams.get(paramName);
  const activeTab = validTabSet.has(requestedTab) ? requestedTab : defaultTab;

  useEffect(() => {
    if (!requestedTab || validTabSet.has(requestedTab)) return;

    const nextSearchParams = new URLSearchParams(searchParams);

    nextSearchParams.delete(paramName);
    setSearchParams(nextSearchParams, { replace: true });
  }, [paramName, requestedTab, searchParams, setSearchParams, validTabSet]);

  const setActiveTab = useCallback(
    (nextTab, options = {}) => {
      if (!validTabSet.has(nextTab) || nextTab === activeTab) return;

      const nextSearchParams = new URLSearchParams(searchParams);

      if (nextTab === defaultTab) {
        nextSearchParams.delete(paramName);
      } else {
        nextSearchParams.set(paramName, nextTab);
      }

      setSearchParams(nextSearchParams, { replace: options.replace === true });
    },
    [
      activeTab,
      defaultTab,
      paramName,
      searchParams,
      setSearchParams,
      validTabSet,
    ]
  );

  return [activeTab, setActiveTab];
};

export default useRouteTab;
